import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hurricaneApi } from '../api';
import { DAMAGE_CONDITIONS, conditionLabel } from '../constants';
import { ROUTES } from '../../../constants/routes';

function HurricaneReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [damageForm, setDamageForm] = useState({
    locationOfDamage: '',
    condition: 'minor',
    notes: '',
  });
  const [damageFiles, setDamageFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await hurricaneApi.getReport(id);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const staff = data?.staff || {};
  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  const addDamage = async (e) => {
    e.preventDefault();
    if (!damageForm.locationOfDamage.trim()) {
      setError('Location of damage is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await hurricaneApi.addDamageItem(id, damageForm);
      const filesToUpload = damageFiles.filter((f) => f && f.size > 0);
      const photoFailures = [];
      for (const file of filesToUpload) {
        try {
          await hurricaneApi.uploadDamagePhoto(id, res.data.id, file);
        } catch (photoErr) {
          photoFailures.push(photoErr.response?.data?.error || file.name || 'upload failed');
        }
      }
      setDamageForm({ locationOfDamage: '', condition: 'minor', notes: '' });
      setDamageFiles([]);
      await load();
      if (photoFailures.length > 0) {
        setError(
          `Damage entry saved. ${photoFailures.length} photo(s) could not be uploaded (photos are optional).`
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add damage');
    } finally {
      setBusy(false);
    }
  };

  const submitDamageReport = async () => {
    setBusy(true);
    setError('');
    try {
      await hurricaneApi.submitReport(id, { outcome: 'has_damage' });
      navigate(ROUTES.HURRICANE);
    } catch (e) {
      setError(e.response?.data?.error || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  if (!data) return <p className="text-danger">{error || 'Not found'}</p>;

  if (data.status === 'submitted') {
    return (
      <div className="alert alert-success">
        Hurricane report submitted.{' '}
        <button type="button" className="btn btn-sm btn-outline-primary ms-2" onClick={() => navigate(ROUTES.HURRICANE)}>
          Back
        </button>
      </div>
    );
  }

  const photosForItem = (itemId) =>
    (data.damageItemPhotos || []).filter((p) => p.item_id === itemId);

  return (
    <div className="app-shell-page">
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card app-card bg-white mb-3">
        <div className="card-header bg-white">Report details</div>
        <div className="card-body small">
          <div className="row">
            <div className="col-md-6">
              <p><strong>Community:</strong> {data.property_name}</p>
              <p><strong>Hurricane:</strong> {data.hurricane_name}</p>
              <p><strong>Site:</strong> {data.location_name || '—'}</p>
              <p><strong>Site address:</strong> {data.location_address || '—'}</p>
              <p><strong>Resident/guest:</strong> {data.resident_name || '—'}</p>
              <p><strong>Date of inspection:</strong> {fmt(data.inspected_at)}</p>
            </div>
            <div className="col-md-6">
              <p><strong>Community manager:</strong> {staff.community_manager_name || '—'}</p>
              <p><strong>Manager email:</strong> {staff.community_manager_email || '—'}</p>
              <p><strong>Assistant manager:</strong> {staff.assistant_manager_name || '—'}</p>
              <p><strong>Assistant email:</strong> {staff.assistant_manager_email || '—'}</p>
              <p><strong>RVP:</strong> {staff.rvp_name || '—'}</p>
              <p><strong>RVP email:</strong> {staff.rvp_email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {data.damageItems?.length > 0 && (
        <div className="card app-card bg-white mb-3">
          <div className="card-header bg-white">Recorded damage ({data.damageItems.length})</div>
          <ul className="list-group list-group-flush small">
            {data.damageItems.map((it) => (
              <li key={it.id} className="list-group-item">
                <strong>{it.location_of_damage}</strong> — {conditionLabel(it.condition)}
                {it.notes && <span className="d-block text-muted">{it.notes}</span>}
                {photosForItem(it.id).length > 0 && (
                  <span className="d-block">{photosForItem(it.id).length} photo(s)</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form className="card app-card bg-white mb-3" onSubmit={addDamage}>
        <div className="card-header bg-white">Damage report</div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Location of damage *</label>
              <input
                className="form-control"
                value={damageForm.locationOfDamage}
                onChange={(e) => setDamageForm({ ...damageForm, locationOfDamage: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Condition *</label>
              <select
                className="form-select"
                value={damageForm.condition}
                onChange={(e) => setDamageForm({ ...damageForm, condition: e.target.value })}
              >
                {DAMAGE_CONDITIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={damageForm.notes} onChange={(e) => setDamageForm({ ...damageForm, notes: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label">
                Photos <span className="text-muted fw-normal">(optional)</span>
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                className="form-control"
                onChange={(e) => setDamageFiles(Array.from(e.target.files || []).filter((f) => f.size > 0))}
              />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <button type="submit" className="btn btn-primary" disabled={busy}>Add damage entry</button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.HURRICANE)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success ms-auto"
              disabled={busy || !data.damageItems?.length}
              onClick={submitDamageReport}
            >
              Submit hurricane report
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default HurricaneReport;
